import React from "react";

/**
 * Accessible on/off switch.
 *
 * NOTE: a new shared component rather than a third copy. The same markup is
 * already duplicated privately in `pages/profile/DocumentList.tsx` and
 * `pages/profile/DocumentForm.tsx`; the feedback module needs it too (the
 * "Share with employee" control), and a third copy would have made the drift
 * permanent. Those two can be pointed here whenever that file is next touched.
 *
 * Styling is lifted verbatim from the existing copies so nothing moves on
 * screen: h-5/w-9 track, h-4/w-4 knob, blue-600 when on.
 */
export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Text rendered beside the switch and wired to it as its label. */
  label?: React.ReactNode;
  /** Smaller, muted line under the label — the "why", not a repeat of the label. */
  hint?: React.ReactNode;
  id?: string;
  className?: string;
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  hint,
  id,
  className = "",
}) => {
  const control = (
    <button
      type="button"
      id={id}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
        checked ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );

  if (!label && !hint) return control;

  return (
    <div className={`flex items-start gap-2.5 ${className}`}>
      {control}
      <div className="min-w-0 leading-tight">
        {label && (
          <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            className="block text-left text-xs font-medium text-gray-700 disabled:cursor-not-allowed dark:text-gray-300"
          >
            {label}
          </button>
        )}
        {hint && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
      </div>
    </div>
  );
};

export default Toggle;
